/**
 * Who is here, and who else can be.
 *
 * Two panels behind one overlay, because they answer the same question from
 * two sides: what this workbench knows about you, and what it knows about the
 * group. Both are deliberately sparse. The roster carries no email addresses
 * and no timestamps — a screen that showed when each colleague was last here
 * would be an attendance log, and in a group of seven that is a record nobody
 * should have to trust everyone else not to screenshot.
 *
 * The seat mechanic is the honest resolution to a real constraint. There are
 * seven representative colours, one each for the whole process, and the seeded
 * cohort holds all of them. So a real person takes over a seat rather than
 * being added beside it, inheriting both the colour and the marks already
 * attributed to it.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Identity, type Person } from '../lib/api.js';

export function Account({
  identity,
  onClose,
}: {
  identity: Identity;
  onClose: () => void;
}) {
  const isRep = identity.role === 'rep';

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: 'rgb(0 0 0 / 0.2)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Your account and the group"
        className="h-full w-full max-w-md overflow-y-auto border-l px-6 py-5"
        style={{ background: 'var(--color-paper)', borderColor: 'var(--color-rule-strong)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <p className="label">Your account</p>
          <button onClick={onClose} className="hit label underline">
            Close
          </button>
        </div>

        <p className="register-reading mt-2" style={{ fontSize: 17 }}>
          {identity.displayName}
        </p>
        <p className="label mt-1">{identity.role}</p>

        <Devices />
        {isRep ? <Roster me={identity.userId} /> : null}
      </div>
    </div>
  );
}

function Devices() {
  const client = useQueryClient();
  const devices = useQuery({ queryKey: ['sessions'], queryFn: api.sessions });
  const revoke = useMutation({
    mutationFn: api.revokeSession,
    onSuccess: () => client.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const list = devices.data?.sessions ?? [];

  return (
    <section className="mt-7 border-t pt-4" style={{ borderColor: 'var(--color-rule)' }}>
      <p className="label">Signed in on</p>
      <p className="mt-1 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Sessions end after twelve hours of inactivity. Sign out anything you do
        not recognise — a staffroom machine, or a device you no longer have.
      </p>

      <ul className="mt-3">
        {list.map((device) => (
          <li
            key={device.id}
            className="flex items-baseline justify-between border-b py-2"
            style={{ borderColor: 'var(--color-rule)' }}
          >
            <span className="text-[13px]">
              {describeDevice(device.userAgent)}
              {device.isCurrent ? <span className="label ml-2">this device</span> : null}
            </span>
            {/* The current device signs out from the header, where the intent
                is unambiguous — revoking it from a list reads as "remove that
                one over there" and 401s the very next click. */}
            {!device.isCurrent ? (
              <button
                onClick={() => revoke.mutate(device.id)}
                className="hit label underline"
                disabled={revoke.isPending}
              >
                Sign out
              </button>
            ) : null}
          </li>
        ))}
        {devices.isPending ? (
          <li className="py-2 text-[13px]" style={{ color: 'var(--color-muted)' }}>
            Loading…
          </li>
        ) : null}
      </ul>
    </section>
  );
}

/**
 * A browser and platform, and nothing more.
 *
 * The full user-agent string is a fingerprint, and the point of this list is
 * only to let someone recognise their own devices.
 */
function describeDevice(agent: string | null): string {
  if (!agent) return 'Unknown device';
  const browser = /Firefox/.test(agent)
    ? 'Firefox'
    : /Edg/.test(agent)
      ? 'Edge'
      : /Chrome/.test(agent)
        ? 'Chrome'
        : /Safari/.test(agent)
          ? 'Safari'
          : 'Browser';
  const platform = /iPhone|iPad/.test(agent)
    ? 'iOS'
    : /Android/.test(agent)
      ? 'Android'
      : /Macintosh/.test(agent)
        ? 'Mac'
        : /Windows/.test(agent)
          ? 'Windows'
          : 'device';
  return `${browser} on ${platform}`;
}

function Roster({ me }: { me: string }) {
  const client = useQueryClient();
  const roster = useQuery({ queryKey: ['people'], queryFn: api.people });
  const refresh = () => client.invalidateQueries({ queryKey: ['people'] });

  const [error, setError] = useState<string | null>(null);
  const act = <T,>(fn: (arg: T) => Promise<unknown>) => (arg: T) => {
    setError(null);
    void fn(arg)
      .then(refresh)
      .catch((e: Error) => setError(e.message));
  };

  const people = roster.data?.people ?? [];
  const free = roster.data?.freeColours ?? 0;

  return (
    <section className="mt-7 border-t pt-4" style={{ borderColor: 'var(--color-rule)' }}>
      <p className="label">The group</p>
      <p className="mt-1 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Seven representative colours, one each for the whole process, so a mark
        is recognisable without reading a name.{' '}
        {free === 0
          ? 'All seven are in use — give an empty seat to the next person rather than adding one beside it.'
          : `${free} ${free === 1 ? 'is' : 'are'} free.`}
      </p>

      {error ? (
        <p className="mt-3 border-l-2 pl-3 text-[12px]" style={{ borderColor: 'var(--color-ink)' }}>
          {error}
        </p>
      ) : null}

      <ul className="mt-3">
        {people.map((person) => (
          <Row
            key={person.id}
            person={person}
            isMe={person.id === me}
            onResend={act(api.resend)}
            onRetire={act(api.retire)}
            onColour={act(api.claimColour)}
            onSeat={act(([id, body]: [string, { email: string; displayName?: string }]) =>
              api.giveSeat(id, body),
            )}
          />
        ))}
      </ul>

      <InviteForm
        onInvite={act(api.invite)}
        // With no colour left, adding a person beside the seats would hand
        // them a blank swatch and quietly break the rail's legend.
        seatsFirst={free === 0}
      />
    </section>
  );
}

function Row({
  person,
  isMe,
  onResend,
  onRetire,
  onSeat,
  onColour,
}: {
  person: Person;
  isMe: boolean;
  onResend: (id: string) => void;
  onRetire: (id: string) => void;
  onSeat: (arg: [string, { email: string; displayName?: string }]) => void;
  onColour: (id: string) => void;
}) {
  const [claiming, setClaiming] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  return (
    <li className="border-b py-2" style={{ borderColor: 'var(--color-rule)' }}>
      <div className="flex items-baseline gap-2">
        <span
          aria-hidden
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{
            background: person.colourToken
              ? `var(--color-${person.colourToken})`
              : 'var(--color-rule-strong)',
          }}
        />
        <span className="text-[13px]">{person.displayName}</span>
        {isMe ? <span className="label">you</span> : null}
        <span className="label ml-auto">{describeStatus(person.status)}</span>
      </div>

      <div className="mt-1 flex gap-3 pl-4">
        {person.status === 'seat' ? (
          <button
            onClick={() => setClaiming((open) => !open)}
            aria-expanded={claiming}
            className="hit label underline"
          >
            {claiming ? 'Cancel' : 'Give this seat to…'}
          </button>
        ) : person.status === 'invited' ? (
          <button onClick={() => onResend(person.id)} className="hit label underline">
            Send another link
          </button>
        ) : null}

        {/* Someone invited while all seven hues were taken is in the workspace
            with none, and nothing else would ever hand them one. */}
        {person.role === 'rep' && !person.colourToken && person.status !== 'seat' ? (
          <button onClick={() => onColour(person.id)} className="hit label underline">
            Give them a colour
          </button>
        ) : null}

        {!isMe ? (
          <button onClick={() => onRetire(person.id)} className="hit label underline">
            Remove
          </button>
        ) : null}
      </div>

      {claiming ? (
        <form
          className="mt-2 pl-4"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            onSeat([person.id, name.trim() ? { email, displayName: name } : { email }]);
            setClaiming(false);
            setEmail('');
            setName('');
          }}
        >
          <input
            type="email"
            required
            aria-label="Their email address"
            placeholder="Their email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border-b bg-transparent py-1 text-[13px] outline-none"
            style={{ borderColor: 'var(--color-rule)' }}
          />
          <input
            type="text"
            aria-label="Their name"
            placeholder={`Their name (leave blank to keep “${person.displayName}”)`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 w-full border-b bg-transparent py-1 text-[13px] outline-none"
            style={{ borderColor: 'var(--color-rule)' }}
          />
          <p className="mt-2 text-[11px]" style={{ color: 'var(--color-muted)' }}>
            They keep {person.displayName}’s colour, and everything already
            attributed to this seat becomes theirs.
          </p>
          <button
            type="submit"
            className="mt-2 border px-3 py-1 text-[12px]"
            style={{ borderColor: 'var(--color-ink)' }}
          >
            Send them a link
          </button>
        </form>
      ) : null}
    </li>
  );
}

function describeStatus(status: Person['status']): string {
  return status === 'active' ? 'signed in' : status === 'invited' ? 'invited' : 'empty seat';
}

function InviteForm({
  onInvite,
  seatsFirst,
}: {
  onInvite: (body: { email: string; displayName: string; role: string }) => void;
  seatsFirst: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('rep');

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="hit mt-4 label underline">
        Invite someone new
      </button>
    );
  }

  return (
    <form
      className="mt-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onInvite({ email, displayName, role });
        setOpen(false);
        setEmail('');
        setDisplayName('');
      }}
    >
      {seatsFirst && role === 'rep' ? (
        <p className="mb-2 text-[11px]" style={{ color: 'var(--color-muted)' }}>
          All seven colours are in use. A new representative will have no colour
          until a seat is freed — give them an empty seat instead if there is one.
        </p>
      ) : null}

      <input
        type="email"
        required
        aria-label="Their email address"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full border-b bg-transparent py-1 text-[13px] outline-none"
        style={{ borderColor: 'var(--color-rule)' }}
      />
      <input
        type="text"
        required
        aria-label="Their name"
        placeholder="Name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        className="mt-2 w-full border-b bg-transparent py-1 text-[13px] outline-none"
        style={{ borderColor: 'var(--color-rule)' }}
      />
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        aria-label="Role"
        className="mt-2 w-full border-b bg-transparent py-1 text-[13px] outline-none"
        style={{ borderColor: 'var(--color-rule)' }}
      >
        <option value="rep">Representative — sees and writes everything</option>
        <option value="staff">Staff — sees what the group has published</option>
        <option value="observer">Observer — reads only</option>
      </select>

      <div className="mt-3 flex gap-3">
        <button
          type="submit"
          className="border px-3 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-ink)' }}
        >
          Send them a link
        </button>
        <button type="button" onClick={() => setOpen(false)} className="hit label underline">
          Cancel
        </button>
      </div>
    </form>
  );
}
